import _ from 'lodash';
import { AST, Relation } from '@cotera/era';
import { Definition, ManifestShorthand, ManifestAST } from './ast';
import { ManifestMeta, _Sink, Schedule } from '@cotera/api';
import type { StreamSpecifier } from '@cotera/api';
import { ScheduledJob } from './scheduled-job';
import { Materialization } from '../materialization/materialization';
import { EventStreamThrottle } from '../builder';

const shorthandToManifest = (shorthand: ManifestShorthand): ManifestAST => {
  const definitions: Record<string, Definition> = _.mapValues(
    shorthand.definitions,
    (def, id) => {
      if (def instanceof Relation) {
        return {
          rel: def.ast,
          displayName: _.startCase(id),
          hidden: false,
        };
      }

      return {
        rel: def.rel.ast,
        displayName: def.displayName ?? _.startCase(id),
        hidden: def.hidden ?? false,
      };
    }
  );

  const materializations = (shorthand.materializations ?? []).map(
    ({ schedule, config }) => ({
      schedule,
      schema: config.config.schema,
      source: config.config.source,
      viewName: config.config.viewName,
    })
  );

  const manifest: ManifestAST = {
    definitions,
    materializations,
    scheduledJobs: shorthand.scheduledJobs ?? [],
    schemas: shorthand.schemas ?? { read: [] },
    eventStreams: [
      ...(shorthand.eventStreams ?? []),
      ...(shorthand.alerts ?? []),
    ],
  };

  return manifest;
};

export type EventStreamDefinition = {
  readonly identifier: AST.ExprFR;
  readonly rel: AST.RelFR;
  readonly when: Schedule;
  readonly onChange: AST.ExprFR;
  readonly sinks: Record<string, _Sink>;
  readonly debounceByIdForSecs?: number;
  readonly throttle?: EventStreamThrottle;
  readonly specifier: StreamSpecifier;
};

export class Manifest {
  readonly definitions: {
    readonly [id: string]: {
      readonly hidden: boolean;
      readonly displayName: string;
      readonly rel: Relation;
      readonly id: string;
    };
  };

  readonly eventStreams: EventStreamDefinition[];

  readonly materializations: readonly Materialization[];

  readonly scheduledJobs: readonly ScheduledJob[];

  readonly schemas: {
    readonly read: string[];
  };

  static fromShorthand(shorthand: ManifestShorthand) {
    return new this(shorthandToManifest(shorthand));
  }

  meta(params: { version: string }): ManifestMeta {
    const manifestMeta: ManifestMeta = {
      version: params.version,
      materializations: this.materializations.map(
        ({ viewName, schedule, schema }) => ({
          schema,
          viewName,
          schedule,
        })
      ),
      definitions: _.mapValues(this.definitions, (def, id) => ({
        id,
        displayName: def.displayName,
        hidden: def.hidden,
        attributes: def.rel.attributes,
      })),
      schemas: this.schemas,
      eventStreams: this.eventStreams.map((s) => ({
        specifier: s.specifier,
        when: s.when,
        sinks: s.sinks,
        rel: s.rel,
        identifier: s.identifier,
      })),
    };

    return manifestMeta;
  }

  getEventStream(name: string): EventStreamDefinition | null {
    const resource = this.eventStreams.find(
      (x) => x.specifier.t === 'manifest' && x.specifier.id === name
    );
    return resource ?? null;
  }

  assumptions(): { readonly [name: string]: AST._TableContract[] } {
    return Object.fromEntries(
      Object.entries(this.definitions).map(([name, def]) => {
        return [name, def.rel.assumptions()];
      })
    );
  }

  constructor(manifest: ManifestAST) {
    this.schemas = manifest.schemas;

    const topLevelDefs = _.mapValues(
      manifest.definitions,
      ({ rel, ...rest }, id) => ({
        ...rest,
        id,
        rel: Relation.fromAst(rel),
      })
    );

    this.definitions = topLevelDefs;
    this.materializations = manifest.materializations;
    this.eventStreams = manifest.eventStreams;
    this.scheduledJobs = [...manifest.scheduledJobs];
  }
}
